import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Loader2, CheckCircle2, XCircle, User, Phone, Clock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { api, formatApiError, asItems, prefixUrl } from "@/lib/api";
import { Skeleton } from "@/components/ui/skeleton";
import { useAuth } from "@/contexts/AuthContext";

const getImageUrl = (urlObj) => {
  if (!urlObj) return "";
  const url = typeof urlObj === "string" ? urlObj : (urlObj.url || urlObj.file_url || urlObj.public_url || "");
  if (!url) return "";
  return prefixUrl(url);
};

export default function OfficeVisitsQueue() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [acceptDialog, setAcceptDialog] = useState({ open: false, sv: null });
  const [agentId, setAgentId] = useState("");
  const [scheduledAt, setScheduledAt] = useState("");
  const [busy, setBusy] = useState("");
  const [error, setError] = useState("");
  const [rejectDialog, setRejectDialog] = useState({ open: false, sv: null });
  const [rejectReason, setRejectReason] = useState("");

  const load = async () => {
    setLoading(true);
    setError("");
    try {
      const role = String(user?.role || "").toLowerCase().replace("office_user", "office");
      const agentRequest = role === "office"
        ? api.get("/office/agents")
        : api.get("/v1/admin/directory/agents", { params: { limit: 200 } });
      const [vRes, aRes] = await Promise.all([
        api.get("/v1/office/site-visits/pending"),
        agentRequest,
      ]);
      setItems(asItems(vRes.data));
      const agentRows = asItems(aRes.data);
      setAgents(agentRows.filter((u) => String(u.role || "agent").toLowerCase() === "agent"));
    } catch (e) {
      const message = formatApiError(e);
      setError(message);
      toast.error(message);
    }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const openAccept = (sv) => {
    setAgentId(""); setScheduledAt(sv.scheduled_at || "");
    setAcceptDialog({ open: true, sv });
  };

  // The UI sends only the selected agent and time. The backend independently
  // verifies office tenancy and active/verified agent membership.
  const submitAccept = async () => {
    if (!agentId) { toast.error("Pick an agent"); return; }
    const sv = acceptDialog.sv;
    if (!sv) return;
    const visitId = sv.site_visit_id || sv.id;
    setBusy(visitId);
    try {
      await api.post(`/v1/office/site-visits/${visitId}/accept`,
        { agent_user_id: agentId, scheduled_at: scheduledAt || null });
      toast.success("Visit accepted and assigned");
      setAcceptDialog({ open: false, sv: null });
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(""); }
  };

  const reject = (sv) => {
    setRejectReason("");
    setRejectDialog({ open: true, sv });
  };

  const submitReject = async () => {
    const sv = rejectDialog.sv;
    if (!sv) return;
    const visitId = sv.site_visit_id || sv.id;
    setBusy(visitId);
    try {
      await api.post(`/v1/office/site-visits/${visitId}/reject`, {
        reason: rejectReason.trim() || "Not accepted by office"
      });
      toast.success("Visit rejected");
      setRejectDialog({ open: false, sv: null });
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(""); }
  };

  return (
    <div className="space-y-6" data-testid="office-visits-queue-page">
      <div>
        <p className="text-xs font-bold uppercase tracking-[0.25em] text-muted-foreground mb-2">Site Visits</p>
        <h1 className="font-display text-4xl font-black tracking-tighter uppercase text-primary">Pending Site Visits</h1>
        <p className="text-muted-foreground mt-2">Accept and assign an agent before a visit can be conducted.</p>
      </div>

      {loading ? (
        <div className="grid gap-3" aria-label="Loading pending site visits">
          {[0, 1, 2].map((row) => (
            <Card key={row} className="p-5 flex gap-4">
              <Skeleton className="h-20 w-20 rounded-md shrink-0" />
              <div className="flex-1 space-y-3"><Skeleton className="h-4 w-24" /><Skeleton className="h-5 w-2/3" /><Skeleton className="h-3 w-1/2" /></div>
            </Card>
          ))}
        </div>
      ) : error ? (
        <Card className="border-destructive/30 bg-destructive/5 p-8 text-center" role="alert">
          <p className="font-bold text-foreground">Site Visits could not be loaded</p>
          <p className="mt-2 text-sm text-muted-foreground">{error}</p>
          <Button type="button" onClick={load} className="mt-4" title="Retry loading Site Visits">Try again</Button>
        </Card>
      ) : items.length === 0 ? (
        <Card className="p-8 text-center text-muted-foreground" data-testid="no-pending-visits">No pending visit requests.</Card>
      ) : (
        <div className="grid gap-3" data-testid="pending-visits-list">
          {items.map((v) => {
            const propLink = v.property?.property_id ? `/properties/${v.property.property_id}` : null;
            return (
              <Card key={v.site_visit_id} className="p-5 flex flex-col md:flex-row md:items-center gap-4"
                data-testid={`pending-visit-${v.site_visit_id}`}>
                {propLink ? (
                  <Link to={propLink} className="w-20 h-20 rounded-md bg-secondary overflow-hidden shrink-0 block group">
                    {v.property?.images?.[0] && <img src={getImageUrl(v.property.images[0])} alt="" loading="lazy" decoding="async" className="w-full h-full object-cover group-hover:scale-105 transition"/>}
                  </Link>
                ) : (
                  <div className="w-20 h-20 rounded-md bg-secondary shrink-0" />
                )}
                <div className="space-y-1.5 min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <Badge variant="outline" className="bg-amber-100 text-amber-900 border-amber-300">REQUESTED</Badge>
                    <span className="text-xs text-muted-foreground font-mono">{v.site_visit_id}</span>
                  </div>
                  {propLink ? (
                    <Link to={propLink} className="font-display font-bold text-primary line-clamp-1 hover:underline">
                      {v.property?.title || "Property"}
                    </Link>
                  ) : (
                    <div className="font-semibold truncate">Property: {v.property_id}</div>
                  )}
                  {v.property?.city && <p className="text-xs uppercase tracking-wider text-muted-foreground">{v.property.city}</p>}
                  <div className="text-sm text-muted-foreground space-y-0.5 pt-1">
                    <div className="flex items-center gap-1.5"><User className="w-3.5 h-3.5"/>{v.visitor_name || "—"}</div>
                    <div className="flex items-center gap-1.5"><Phone className="w-3.5 h-3.5"/>{v.visitor_phone || "—"}</div>
                    <div className="flex items-center gap-1.5"><Clock className="w-3.5 h-3.5"/>{v.scheduled_at || "—"}</div>
                  </div>
                </div>
                <div className="flex gap-2 shrink-0 self-end md:self-center">
                  <Button size="sm" className="gap-2 font-bold" onClick={() => openAccept(v)} disabled={busy === v.site_visit_id}
                    data-testid={`btn-accept-visit-${v.site_visit_id}`}>
                    <CheckCircle2 className="w-3.5 h-3.5"/> Accept & Assign
                  </Button>
                  <Button size="sm" variant="outline" className="gap-2 font-bold" onClick={() => reject(v)} disabled={busy === v.site_visit_id}
                    data-testid={`btn-reject-visit-${v.site_visit_id}`}>
                    <XCircle className="w-3.5 h-3.5"/> Reject
                  </Button>
                </div>
              </Card>
            );
          })}
        </div>
      )}

      <Dialog open={acceptDialog.open} onOpenChange={(v) => !v && setAcceptDialog({ open: false, sv: null })}>
        <DialogContent data-testid="accept-visit-dialog">
          <DialogHeader>
            <DialogTitle className="font-display tracking-tight uppercase">Accept Visit & Assign Agent</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1.5">
              <Label className="text-xs font-bold uppercase tracking-widest text-muted-foreground">Assign to Agent</Label>
              <select
                value={agentId}
                onChange={(e) => setAgentId(e.target.value)}
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                data-testid="select-agent"
              >
                <option value="">Pick an agent</option>
                {agents.map((a) => (
                  <option key={a.user_id} value={a.user_id} data-testid={`agent-option-${a.user_id}`}>
                    {a.name || "Unnamed agent"}
                  </option>
                ))}
              </select>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-bold uppercase tracking-widest text-muted-foreground">Scheduled at</Label>
              <Input type="datetime-local" value={scheduledAt?.slice(0,16) || ""} onChange={(e) => setScheduledAt(e.target.value)}
                data-testid="input-scheduled-at" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAcceptDialog({ open: false, sv: null })}>Cancel</Button>
            <Button onClick={submitAccept} disabled={!agentId || busy} className="font-bold gap-2" data-testid="btn-confirm-accept">
              {busy ? <Loader2 className="w-4 h-4 animate-spin"/> : <CheckCircle2 className="w-4 h-4"/>}
              Accept & Assign
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={rejectDialog.open} onOpenChange={(open) => !open && setRejectDialog({ open: false, sv: null })}>
        <DialogContent data-testid="reject-visit-dialog">
          <DialogHeader><DialogTitle>Reject Site Visit</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground">The buyer will see that this request was cancelled. Add a short operational reason.</p>
          <div className="space-y-1.5">
            <Label htmlFor="reject-visit-reason">Reason</Label>
            <Input id="reject-visit-reason" value={rejectReason} maxLength={500}
              onChange={(event) => setRejectReason(event.target.value)} placeholder="Reason for rejecting this request" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRejectDialog({ open: false, sv: null })}>Keep request</Button>
            <Button variant="destructive" onClick={submitReject} disabled={Boolean(busy)}>Reject request</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
